import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon } from '../icons/icons.tsx';
import { cx } from '../lib/cx.ts';
import { useUiT } from '../i18n/index.tsx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'success' | 'accent-soft' | 'danger' | 'danger-ghost';
export type ControlSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ControlSize;
  icon?: string | null;
  iconRight?: string | null;
  loading?: boolean;
  block?: boolean;
  /** Forces a visual state (design previews only). */
  state?: 'hover' | 'pressed';
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, iconRight, loading, block, state, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'md-btn',
        'md-btn--' + variant,
        size !== 'md' && 'md-btn--' + size,
        block && 'md-btn--block',
        state && 'is-' + state,
        className,
      )}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <span className="md-spin" aria-hidden="true" />
      ) : icon ? (
        <Icon name={icon} size={size === 'sm' ? 16 : 18} />
      ) : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={16} /> : null}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: string;
  /** Accessible name; also shown as the native tooltip unless `tooltip={false}`. */
  label: string;
  size?: ControlSize;
  active?: boolean;
  danger?: boolean;
  round?: boolean;
  dot?: boolean;
  iconSize?: number;
  state?: 'hover' | 'pressed';
  /** Use the native title tooltip (default true). Pass false when wrapped in <Tooltip>. */
  nativeTitle?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 'md', active, danger, round, dot, state, className, iconSize, nativeTitle = true, type, ...rest },
  ref,
) {
  const s = iconSize || (size === 'sm' ? 18 : size === 'lg' ? 22 : 20);
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={nativeTitle ? label : undefined}
      aria-pressed={active === undefined ? undefined : active}
      {...rest}
      className={cx(
        'md-ibtn',
        size !== 'md' && 'md-ibtn--' + size,
        round && 'md-ibtn--round',
        active && 'is-active',
        danger && 'is-danger',
        state && 'is-' + state,
        className,
      )}
    >
      <Icon name={icon} size={s} />
      {dot ? <span className="md-dot-badge" aria-hidden="true" /> : null}
    </button>
  );
});

export function Spinner({ size = 14, label }: { size?: number; label?: string }) {
  const t = useUiT();
  return (
    <span
      className="md-spin md-spin--standalone"
      role="status"
      aria-label={label ?? t('common.loading')}
      style={{ width: size, height: size }}
    />
  );
}

/** `.md-split` wrapper that joins two Buttons (primary action + options). */
export function SplitButton({ children }: { children: ReactNode }) {
  return <span className="md-split">{children}</span>;
}
